import { LitElement, html, css } from "lit";

/**
 * RDCheckbox - Project-wide checkbox component
 * Supports multiple variants: checkbox, toggle (iPhone-style), or both side-by-side
 * Fully theme-aware and customizable
 */
export class RDCheckbox extends LitElement {
    static properties = {
        checked: { type: Boolean, reflect: true },
        label: { type: String },
        variant: { type: String }, // 'checkbox' | 'toggle' | 'both'
        disabled: { type: Boolean, reflect: true },
        size: { type: String }, // 'small' | 'medium' | 'large'
        value: { type: String },
        name: { type: String },
    };

    static styles = css`
    :host {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-family: var(--font-family, system-ui, -apple-system, sans-serif);
      cursor: pointer;
      user-select: none;
    }

    :host([disabled]) {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }

    .checkbox-container {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
    }

    /* Standard Checkbox */
    .checkbox-wrapper {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }

    .checkbox {
      appearance: none;
      width: var(--checkbox-size, 16px);
      height: var(--checkbox-size, 16px);
      border: 2px solid var(--border-color, #ccc);
      border-radius: 3px;
      background: var(--bg-panel, #fff);
      cursor: pointer;
      transition: all 0.2s ease;
      position: relative;
      margin: 0;
      flex-shrink: 0;
    }

    .checkbox:hover:not(:disabled) {
      border-color: var(--primary-color, #007bff);
    }

    .checkbox:checked {
      background: var(--primary-color, #007bff);
      border-color: var(--primary-color, #007bff);
    }

    .checkbox:checked::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -60%) rotate(45deg);
      width: 4px;
      height: 8px;
      border: solid white;
      border-width: 0 2px 2px 0;
    }

    .checkbox:focus-visible {
      outline: 2px solid var(--primary-color, #007bff);
      outline-offset: 2px;
    }

    /* Toggle Switch (iPhone-like) */
    .toggle-wrapper {
      position: relative;
      display: inline-flex;
      align-items: center;
    }

    .toggle {
      appearance: none;
      width: var(--toggle-width, 36px);
      height: var(--toggle-height, 20px);
      background: var(--border-color, #ccc);
      border-radius: var(--toggle-height, 20px);
      position: relative;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      margin: 0;
      border: none;
      flex-shrink: 0;
    }

    .toggle::before {
      content: '';
      position: absolute;
      width: var(--toggle-knob-size, 16px);
      height: var(--toggle-knob-size, 16px);
      background: white;
      border-radius: 50%;
      top: 2px;
      left: 2px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    .toggle:hover:not(:disabled):not(:checked) {
      background: var(--text-secondary, #999);
    }

    .toggle:checked {
      background: var(--primary-color, #007bff);
    }

    .toggle:checked:hover:not(:disabled) {
      background: var(--primary-hover, #0056b3);
    }

    .toggle:checked::before {
      left: calc(var(--toggle-width, 36px) - var(--toggle-knob-size, 16px) - 2px);
    }

    .toggle:focus-visible {
      outline: 2px solid var(--primary-color, #007bff);
      outline-offset: 2px;
    }

    /* Label */
    .label {
      color: var(--text-main, #333);
      font-size: 13px;
      cursor: pointer;
      flex: 1;
      line-height: 1.4;
    }

    /* Size Variants */
    :host([size="small"]) {
      --checkbox-size: 14px;
      --toggle-width: 30px;
      --toggle-height: 16px;
      --toggle-knob-size: 12px;
      gap: 6px;
    }

    :host([size="small"]) .label {
      font-size: 12px;
    }

    :host([size="small"]) .checkbox-container {
      gap: 6px;
    }

    :host([size="medium"]) {
      --checkbox-size: 16px;
      --toggle-width: 36px;
      --toggle-height: 20px;
      --toggle-knob-size: 16px;
      gap: 8px;
    }

    :host([size="medium"]) .label {
      font-size: 13px;
    }

    :host([size="medium"]) .checkbox-container {
      gap: 8px;
    }

    :host([size="large"]) {
      --checkbox-size: 18px;
      --toggle-width: 42px;
      --toggle-height: 24px;
      --toggle-knob-size: 20px;
      gap: 10px;
    }

    :host([size="large"]) .label {
      font-size: 14px;
    }

    :host([size="large"]) .checkbox-container {
      gap: 10px;
    }

    /* Both variant - show checkbox and toggle side by side */
    .both-wrapper {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .divider {
      width: 1px;
      height: 16px;
      background: var(--border-color, #e0e0e0);
    }
  `;

    constructor() {
        super();
        this.checked = false;
        this.label = '';
        this.variant = 'both'; // 'checkbox' | 'toggle' | 'both'
        this.disabled = false;
        this.size = 'medium';
        this.value = '';
        this.name = '';
    }

    _handleChange(event) {
        if (this.disabled) return;

        this.checked = event.target.checked;

        // Dispatch native-like change event
        this.dispatchEvent(
            new Event('change', {
                bubbles: true,
                composed: true,
            })
        );

        // Also dispatch custom event with details
        this.dispatchEvent(
            new CustomEvent('checkbox-change', {
                detail: {
                    checked: this.checked,
                    value: this.value,
                    name: this.name,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    _handleClick(event) {
        // Allow label clicks to propagate naturally
        if (event.target.classList.contains('label')) {
            return;
        }
    }

    render() {
        const checkboxId = `checkbox-${this.value || Math.random().toString(36).substr(2, 9)}`;
        const toggleId = `toggle-${this.value || Math.random().toString(36).substr(2, 9)}`;

        return html`
      <div class="checkbox-container" @click="${this._handleClick}">
        ${this.variant === 'checkbox' || this.variant === 'both'
                ? html`
              <div class="checkbox-wrapper">
                <input
                  id="${checkboxId}"
                  type="checkbox"
                  class="checkbox"
                  .checked="${this.checked}"
                  ?disabled="${this.disabled}"
                  @change="${this._handleChange}"
                  name="${this.name || ''}"
                  value="${this.value || ''}"
                />
              </div>
            `
                : ''}
        
        ${this.variant === 'both' ? html`<div class="divider"></div>` : ''}
        
        ${this.variant === 'toggle' || this.variant === 'both'
                ? html`
              <div class="toggle-wrapper">
                <input
                  id="${toggleId}"
                  type="checkbox"
                  class="toggle"
                  .checked="${this.checked}"
                  ?disabled="${this.disabled}"
                  @change="${this._handleChange}"
                  name="${this.name || ''}"
                  value="${this.value || ''}"
                />
              </div>
            `
                : ''}
        
        ${this.label
                ? html`
              <label 
                for="${this.variant === 'toggle' ? toggleId : checkboxId}" 
                class="label"
              >
                ${this.label}
              </label>
            `
                : ''}
      </div>
    `;
    }
}

customElements.define('rd-checkbox', RDCheckbox);
